import { useState, useEffect } from 'react';
import { getImageUrl } from '../../services/firebase';
import type { WatchReference } from '../../types';

export interface ReferenceCardProps {
  reference: WatchReference;
  onClick: () => void;
  selected?: boolean;
  onSelect?: () => void;
  /** Render as a variant card (subtle border) inside a parent group. */
  variant?: boolean;
}

/**
 * Grid-view card for a single reference. Shared by the brand's reference browser
 * and the reference editor's Variants panel, so a variant looks the same
 * wherever it's listed.
 */
export function ReferenceCard({ reference, onClick, selected, onSelect, variant }: ReferenceCardProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (reference.images && reference.images[0]?.storagePath) {
      getImageUrl(reference.images[0].storagePath)
        .then(url => setImageUrl(url))
        .catch(err => console.error('Failed to load image:', err));
    } else {
      setImageUrl(null);
    }
  }, [reference.images]);

  return (
    <div
      className={`bg-white shadow rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${
        selected ? 'ring-2 ring-brand-500' : ''
      } ${variant ? 'border border-gray-200' : ''}`}
      onClick={onClick}
    >
      {/* Image */}
      <div className="aspect-square bg-gray-100 relative">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={reference.reference}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <svg
              className="w-12 h-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
        {/* Checkbox */}
        {onSelect !== undefined && (
          <div
            className="absolute top-2 left-2"
            onClick={(e) => { e.stopPropagation(); onSelect(); }}
          >
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => {}}
              className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
            />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <h3 className="font-medium text-gray-900 truncate">
          {reference.reference}
        </h3>
        <p className="text-sm text-gray-600 truncate">
          {reference.model || reference.productName || '-'}
        </p>
        {reference.collection && (
          <p className="text-xs text-gray-500 truncate">
            {reference.collection}
          </p>
        )}
      </div>
    </div>
  );
}
